import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'
import type {RecordSourceSelectorProxy} from 'relay-runtime'

type AgentAssignmentAssigner = {
  __typename: string
  id: string
  login: string
}

// Adds an agent assignment to the issue's agentAssignments in the Relay store
// Does nothing if the agent is already present in agentAssignments
// Performs a local update only; does not call any mutations
export const addAgentAssignmentToIssueLocally = (
  store: RecordSourceSelectorProxy<object>,
  issueId: string,
  assignee: AssigneePickerBaseAssignee,
  assigner?: AgentAssignmentAssigner,
) => {
  const issueRecord = store.get(issueId)
  if (!issueRecord) return

  let agentAssignments = issueRecord.getLinkedRecords('agentAssignments') || []

  const alreadyExists = agentAssignments.some(record => {
    const existingAssignee = record?.getLinkedRecord('assignee')
    return existingAssignee && existingAssignee.getValue('id') === assignee.id
  })
  if (alreadyExists) return

  // Create or reuse the AgentAssignment record with a unique client ID
  const assignmentClientId = `client:AgentAssignment:${issueId}:${assignee.id}`
  const assignmentRecord = store.get(assignmentClientId) ?? store.create(assignmentClientId, 'AgentAssignment')

  // Create or reuse the assignee record
  const assigneeRecord = store.get(assignee.id) ?? createAssigneeRecord(store, assignee)
  assignmentRecord.setLinkedRecord(assigneeRecord, 'assignee')

  // Create or reuse the assigner record
  if (assigner) {
    const assignerRecord = store.get(assigner.id) ?? store.create(assigner.id, assigner.__typename)
    assignerRecord.setValue(assigner.id, 'id')
    assignerRecord.setValue(assigner.login, 'login')
    assignerRecord.setValue(assigner.__typename, '__typename')
    assignmentRecord.setLinkedRecord(assignerRecord, 'assigner')
  } else {
    assignmentRecord.setValue(null, 'assigner')
  }

  agentAssignments = [...agentAssignments, assignmentRecord]
  issueRecord.setLinkedRecords(agentAssignments, 'agentAssignments')
}

// Adds the given assignees to the issue's assignedActors in the Relay store
// Does nothing if an assignee is already assigned
// Performs a local update only; does not call any mutations
// No limits on the number of assignees are enforced here
export const addAssigneesToIssueLocally = (
  store: RecordSourceSelectorProxy<object>,
  issueId: string,
  assignees: AssigneePickerBaseAssignee[],
) => {
  const issueRecord = store.get(issueId)
  if (issueRecord) {
    const assignedActors = issueRecord.getLinkedRecord('assignedActors(first:20)')
    if (assignedActors) {
      let nodes = assignedActors.getLinkedRecords('nodes') || []

      for (const assignee of assignees) {
        const assigneeAlreadyAssigned = nodes.some(node => node && node.getValue('id') === assignee.id)
        if (!assigneeAlreadyAssigned) {
          let assigneeRecord = store.get(assignee.id)
          if (!assigneeRecord) {
            assigneeRecord = createAssigneeRecord(store, assignee)
          }
          nodes = [...nodes, assigneeRecord]
        }
      }

      const sortedNodes = nodes.sort((a, b) => {
        const loginA = (a?.getValue('login') as string) || ''
        const loginB = (b?.getValue('login') as string) || ''
        return loginA.toLowerCase().localeCompare(loginB.toLowerCase())
      })
      assignedActors.setLinkedRecords(sortedNodes, 'nodes')
    }
  }
}

const createAssigneeRecord = (store: RecordSourceSelectorProxy<object>, assignee: AssigneePickerBaseAssignee) => {
  const assigneeRecord = store.create(assignee.id, assignee.__typename)

  const propertiesToSet: AssigneePickerBaseAssignee = {
    id: assignee.id,
    login: assignee.login,
    name: assignee.name,
    avatarUrl: assignee.avatarUrl,
    isCopilot: assignee.isCopilot,
    isAgent: assignee.isAgent,
    profileResourcePath: assignee.profileResourcePath,
    __typename: assignee.__typename,
    displayName: assignee.displayName,
  }
  for (const [key, value] of Object.entries(propertiesToSet)) {
    assigneeRecord.setValue(value, key)
  }
  return assigneeRecord
}
